import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import sbeeLogo from "../../assets/logo.png";
import { PURCHASE_API, MASTER_API } from "../../apiConfig";

// TODO: confirm the real mount path for goodsRequest.routes.js
// (look for app.use('/something', goodsRequestRoutes) in your
// purchase-production-service app.js/server.js) and update this.
const API_BASE = `${PURCHASE_API}/goods-request`;

// ── Formats any date string (ISO "2026-07-01", with or without a
//    time component) into dd/mm/yyyy for display. Leaves blank/invalid
//    values untouched.
const formatDate = (value) => {
    if (!value) return "";
    const datePart = String(value).split("T")[0];
    const match = datePart.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (match) {
        const [, yyyy, mm, dd] = match;
        return `${dd}/${mm}/${yyyy}`;
    }
    return value;
};

// ── Formats any qty value to a fixed 2 decimal places for display.
//    Leaves blank/non-numeric values untouched.
const formatQty = (value) => {
    if (value === null || value === undefined || value === "") return "";
    const num = Number(value);
    return Number.isNaN(num) ? value : num.toFixed(2);
};

// ── Parses a row's sales_order_numbers value (stored as a JSON array
//    string, e.g. '["SO001","SO002","SO003"]') into a plain array for
//    rendering as separate grid cells. Falls back to a comma-split for
//    any legacy/plain-string value, and to [] when there's nothing.
const parseSalesOrders = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value.filter(Boolean);
    try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) return parsed.filter(Boolean);
    } catch (err) {
        // not JSON — fall through to comma-split below
    }
    return String(value)
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean);
};

// ── Parses a row's production_order_number value into a plain,
//    comma-joined display string. A clubbed request line (same
//    Workcenter + Product Code) stores this as a JSON array string
//    (e.g. '["PRD0008","PRD0007"]'); an un-clubbed/legacy row is just
//    a plain string. Both are handled so the printed form never shows
//    raw quotes/brackets.
// ── Parses a production_order_number value into a clean, de-duplicated
//    array of order numbers. Handles a real array, a JSON array string
//    ('["PRD1","PRD2"]'), a double-encoded JSON string, and a plain
//    comma-separated string; stray brackets/quotes are stripped so the
//    printed form never shows raw JSON.
const parseProductionOrders = (value) => {
    if (!value) return [];
    let v = value;
    for (let i = 0; i < 2 && typeof v === "string"; i++) {
        try {
            v = JSON.parse(v);
        } catch (err) {
            break;
        }
    }
    const list = Array.isArray(v) ? v : String(v).split(",");
    return [
        ...new Set(
            list
                .map((x) => String(x).replace(/[\[\]"']/g, "").trim())
                .filter(Boolean)
        ),
    ];
};

const formatProductionOrders = (value) => {
    if (!value) return "";
    if (Array.isArray(value)) return value.filter(Boolean).join(", ");
    try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) return parsed.filter(Boolean).join(", ");
    } catch (err) {
        // not JSON — fall through to returning it as-is
    }
    return String(value);
};



const styles = {

    wrapper: {
        background: "#e9e9e9",
        minHeight: "100vh",
        padding: "24px",
        boxSizing: "border-box"
    },

    toolbar: {
        maxWidth: "850px",
        margin: "0 auto 12px auto",
        display: "flex",
        justifyContent: "flex-end"
    },

    printBtn: {
        background: "#1d4ed8",
        color: "#fff",
        border: "none",
        borderRadius: "4px",
        padding: "8px 18px",
        fontSize: "14px",
        cursor: "pointer"
    },

    status: {
        maxWidth: "850px",
        margin: "0 auto",
        textAlign: "center",
        padding: "24px",
        fontSize: "14px",
        color: "#444"
    },

    error: {
        color: "#b91c1c"
    },

    slip: {
        background: "#fff",
        maxWidth: "850px",
        margin: "0 auto",
        padding: "28px 32px",
        border: "1px solid #000",
        fontFamily: "Arial, Helvetica, sans-serif",
        color: "#000",
        boxSizing: "border-box"
    },

    header: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "16px",
        borderBottom: "1px solid #000",
        paddingBottom: "10px"
    },

    logo: {
        height: "48px",
        width: "auto",
        objectFit: "contain"
    },

    titleBlock: {
        textAlign: "center"
    },

    title: {
        fontSize: "18px",
        fontWeight: "bold",
        letterSpacing: "0.5px",
        fontFamily: "'Good Times', 'Segoe UI', Calibri, Arial, sans-serif"
    },

    subtitle: {
        fontSize: "13px",
        fontWeight: "bold",
        marginTop: "4px"
    },

    address: {
        fontSize: "11px",
        fontWeight: "normal",
        marginTop: "4px",
        color: "#333"
    },

    table: {
        width: "100%",
        borderCollapse: "collapse",
        tableLayout: "fixed"
    },

    metaTd: {
        border: "1px solid #000",
        borderTop: "none",
        padding: "6px 8px",
        fontSize: "13px",
        verticalAlign: "middle"
    },

    metaTdFirstRow: {
        border: "1px solid #000",
        padding: "6px 8px",
        fontSize: "13px",
        verticalAlign: "middle"
    },

    metaLabel: {
        fontWeight: "bold",
        width: "18%"
    },

    metaNote: {
        fontWeight: "normal",
        fontSize: "11px"
    },

    instruction: {
        fontWeight: "bold",
        fontSize: "13px",
        padding: "8px 4px",
        borderLeft: "1px solid #000",
        borderRight: "1px solid #000"
    },

    th: {
        border: "1px solid #000",
        padding: "6px 6px",
        fontSize: "12px",
        textAlign: "center",
        fontWeight: "bold",
        background: "#f5f5f5"
    },

    td: {
        border: "1px solid #000",
        padding: "6px 6px",
        fontSize: "12px",
        textAlign: "center",
        height: "26px"
    },

    tdLeft: {
        border: "1px solid #000",
        padding: "6px 6px",
        fontSize: "12px",
        textAlign: "left",
        height: "26px"
    },

    colSl: {
        width: "7%"
    },

    footerTd: {
        border: "1px solid #000",
        borderTop: "none",
        padding: "10px 8px",
        fontSize: "13px"
    },

    footerTdFirstRow: {
        border: "1px solid #000",
        padding: "10px 8px",
        fontSize: "13px"
    },

    footerLabel: {
        fontWeight: "bold",
        width: "22%"
    },

    salesOrderRow: {
        border: "1px solid #000",
        borderTop: "none",
        display: "flex",
        alignItems: "stretch"
    },

    salesOrderLabelCell: {
        border: "none",
        borderRight: "1px solid #000",
        padding: "6px 8px",
        fontSize: "13px",
        fontWeight: "bold",
        width: "18%",
        flex: "0 0 18%",
        boxSizing: "border-box"
    },

    salesOrderGrid: {
        flex: "1 1 auto",
        display: "flex",
        flexWrap: "wrap"
    },

    salesOrderCell: {
        border: "none",
        borderRight: "1px solid #000",
        padding: "6px 8px",
        fontSize: "13px",
        boxSizing: "border-box",
        overflowWrap: "anywhere"
    }

};

export default function GoodsRequestForm({ requestNo: requestNoProp } = {}) {

    const params = useParams();
    const requestNo = requestNoProp || params.requestNo;

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [unit1Location, setUnit1Location] = useState(null); // from Configuration > Location Master (Unit 1 / Regd. Office)

    useEffect(() => {

        const fetchData = async () => {

            try {

                setLoading(true);
                setError("");

                const res = await axios.get(
                    `${API_BASE}/requested/${requestNo}`
                );

                setRows(res.data?.data || []);

            } catch (err) {

                console.log(err);
                setError("Failed to load goods request data");

            } finally {

                setLoading(false);

            }

        };

        if (requestNo) {
            fetchData();
        }

    }, [requestNo]);

    useEffect(() => {
        const loadUnit1Location = async () => {
            try {
                const res = await axios.get(`${MASTER_API}/location-master`);
                const locations = Array.isArray(res.data) ? res.data : [];
                const unit1 = locations.find((loc) => (loc.location_label || "").toLowerCase().includes("1")) || null;
                setUnit1Location(unit1);
            } catch (err) {
                console.error("LOCATION MASTER FETCH ERROR", err);
                setUnit1Location(null);
            }
        };
        loadUnit1Location();
    }, []);

    // Unit 1 (Regd. Office) address, shown under the company name. Falls
    // back to the static text only if Location Master hasn't been set up yet.
    const unit1Name = unit1Location?.company_name || "Dizura";
    const unit1AddressText = unit1Location
        ? `${unit1Location.address_line1}${unit1Location.address_line2 ? ", " + unit1Location.address_line2 : ""}, ${unit1Location.city}${unit1Location.pin_code ? " - " + unit1Location.pin_code : ""}, ${unit1Location.state}`
        : "No.282 & 283, KIADB Industrial Area, Phase 2, Harohalli, Kanakapura Taluk, Ramanagar - 562112, Karnataka, INDIA";

    const first = rows[0] || {};

    // Union of Production Orders across every line (a request can now
    // carry several lines, each with its own PO list).
    const jobCardNumbers = [
        ...new Set(rows.flatMap((r) => parseProductionOrders(r.production_order_number)))
    ];

    const isEmbedded = !!requestNoProp;

    const inner = (
        <>
            {!isEmbedded && (
                <div style={styles.toolbar} className="gr-no-print">
                    <button style={styles.printBtn} onClick={() => window.print()}>
                        Print
                    </button>
                </div>
            )}

            {loading && (
                <div style={styles.status}>Loading...</div>
            )}

            {error && (
                <div style={{ ...styles.status, ...styles.error }}>
                    {error}
                </div>
            )}

            {!loading && !error && rows.length === 0 && (
                <div style={styles.status}>
                    No goods request found for {requestNo}
                </div>
            )}

            {!loading && !error && rows.length > 0 && (
                <div style={styles.slip} id="gr-print-area">

                    <div style={styles.header}>
                        <img
                            src={sbeeLogo}
                            alt="SBEE Cables logo"
                            style={styles.logo}
                        />
                        <div style={styles.titleBlock}>
                            <div style={styles.title}>
                                {unit1Name}
                            </div>
                            <div style={styles.address}>
                                {unit1AddressText}
                            </div>
                            <div style={styles.subtitle}>
                                MATERIAL INDENT
                            </div>
                        </div>
                    </div>

                    <table style={styles.table}>
                        <tbody>
                            <tr>
                                <td style={{ ...styles.metaTdFirstRow, ...styles.metaLabel }}>
                                    Indent No :
                                    <span style={styles.metaNote}></span>
                                </td>
                                <td style={styles.metaTdFirstRow}>
                                    {first.request_no || ""}
                                </td>
                            </tr>
                            {/* <tr>
        <td style={{ ...styles.metaTd, ...styles.metaLabel }}>
            Start Date
        </td>
        <td style={styles.metaTd}>
            {formatDate(first.production_start_date)}
        </td>
        <td style={{ ...styles.metaTd, ...styles.metaLabel }}>
            End Date
        </td>
        <td style={styles.metaTd}>
            {formatDate(first.production_end_date)}
        </td>
    </tr> */}
                        </tbody>
                    </table>

                    {/* ── Job Card No row: every Production Order across all
                            lines of this request, each in its own wrapped cell
                            so a long list stays inside the page ── */}
                    <div style={styles.salesOrderRow}>
                        <div style={styles.salesOrderLabelCell}>
                            Job Card No:
                        </div>
                        <div style={styles.salesOrderGrid}>
                            {jobCardNumbers.length > 0 ? (
                                jobCardNumbers.map((po, i) => (
                                    <div key={i} style={styles.salesOrderCell}>
                                        {po}
                                    </div>
                                ))
                            ) : (
                                <div style={styles.salesOrderCell}>-</div>
                            )}
                        </div>
                    </div>

                    {/* ── Sales Order row: each sales order for this production
                            order shown as its own grid cell (not comma-joined) ── */}
                    <div style={styles.salesOrderRow}>
                        <div style={styles.salesOrderLabelCell}>
                            Sales Order
                        </div>
                        <div style={styles.salesOrderGrid}>
                            {parseSalesOrders(first.sales_order_numbers).length > 0 ? (
                                parseSalesOrders(first.sales_order_numbers).map((so, i) => (
                                    <div key={i} style={styles.salesOrderCell}>
                                        {so}
                                    </div>
                                ))
                            ) : (
                                <div style={styles.salesOrderCell}>-</div>
                            )}
                        </div>
                    </div>

                    <div style={styles.instruction}>
                        Kindly issue The Following Items
                    </div>

                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={{ ...styles.th, ...styles.colSl }}>
                                    Sl No.
                                </th>
                                <th style={styles.th}>Product Code</th>
                                <th style={styles.th}>Description</th>
                                <th style={styles.th}>Workcenter</th>
                                <th style={styles.th}>Storage Location</th>
                                <th style={styles.th}>UOM</th>
                                <th style={styles.th}>Actual Qty</th>
                                <th style={styles.th}>Requested Qty</th>
                                <th style={styles.th}>Issued Qty</th>
                                <th style={styles.th}>Confirmed Qty</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r, idx) => (
                                <tr key={r.id || idx}>
                                    <td style={styles.td}>{idx + 1}</td>
                                    <td style={styles.td}>
                                        {r.product_code}
                                    </td>
                                    <td style={styles.tdLeft}>
                                        {r.description}
                                    </td>
                                    <td style={styles.td}>
                                        {r.workcenter_name || ""}
                                    </td>
                                    <td style={styles.td}>
                                        {r.storage_code
                                            ? `${r.storage_code} - ${r.storage_location || ""}`
                                            : (r.storage_location || "")}
                                    </td>
                                    <td style={styles.td}>{r.uom}</td>
                                    <td style={styles.td}>{formatQty(r.qty)}</td>
                                    <td style={styles.td}>
                                        {formatQty(r.request_qty)}
                                    </td>
                                    <td style={styles.td}>
                                        {formatQty(r.issued_qty)}
                                    </td>
                                    <td style={styles.td}>
                                        {formatQty(r.confirm_qty)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <table style={styles.table}>
                        <tbody>
                            <tr>
                                <td
                                    style={{
                                        ...styles.footerTdFirstRow,
                                        ...styles.footerLabel
                                    }}
                                >
                                    Requested By
                                </td>
                                <td style={styles.footerTdFirstRow}>
                                    {first.created_by || ""}
                                </td>
                                <td
                                    style={{
                                        ...styles.footerTdFirstRow,
                                        ...styles.footerLabel
                                    }}
                                >
                                    Request Date
                                </td>
                                <td style={styles.footerTdFirstRow}>
                                    {formatDate(first.request_date)}
                                </td>
                            </tr>
                            <tr>
                                <td
                                    style={{
                                        ...styles.footerTd,
                                        ...styles.footerLabel
                                    }}
                                >
                                    Issued By
                                </td>
                                <td style={styles.footerTd}>
                                    {Number(first.issued_qty) > 0 ? (first.issued_by || "") : ""}
                                </td>
                                <td
                                    style={{
                                        ...styles.footerTd,
                                        ...styles.footerLabel
                                    }}
                                >
                                    Issued Date
                                </td>
                                <td style={styles.footerTd}>
                                    {Number(first.issued_qty) > 0 ? formatDate(first.issue_date) : ""}
                                </td>
                            </tr>
                            <tr>
                                <td
                                    style={{
                                        ...styles.footerTd,
                                        ...styles.footerLabel
                                    }}
                                >
                                    Received By
                                </td>
                                <td style={styles.footerTd}>
                                    {Number(first.confirm_qty) > 0 ? (first.confirmed_by || "") : ""}
                                </td>
                                <td
                                    style={{
                                        ...styles.footerTd,
                                        ...styles.footerLabel
                                    }}
                                >
                                    Received Date
                                </td>
                                <td style={styles.footerTd}>
                                    {Number(first.confirm_qty) > 0 ? formatDate(first.confirm_date) : ""}
                                </td>
                            </tr>
                        </tbody>
                    </table>

                </div>
            )}

            <style>
                {`
                    @media print {
                        .gr-no-print {
                            display: none !important;
                        }
                        body {
                            background: #fff !important;
                        }
                        @page {
                            size: A4;
                            margin: 14mm;
                        }
                    }
                `}
            </style>
        </>
    );

    if (isEmbedded) {
        return (
            <div style={{ padding: "16px", overflowY: "auto", height: "100%" }}>
                {inner}
            </div>
        );
    }

    return (
        <div style={styles.wrapper}>
            {inner}
        </div>
    );

}